import React, { useRef, useState } from 'react';
import { AlertTriangle, BookOpen } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

const errorMessage = (err) => typeof err.response?.data?.detail === 'string'
  ? err.response.data.detail : 'No se pudo completar la solicitud. Consulta la publicación antes de reintentar.';

function Attributes({ attributes = [] }) {
  return <dl className="mt-3 grid gap-1 text-sm text-slate-300">
    {attributes.map((attr, index) => <div key={`${attr.id}-${index}`}>
      <dt className="inline text-slate-500">{attr.name || attr.id}: </dt>
      <dd className="inline">{attr.value_name || attr.values?.map(v => v.name).filter(Boolean).join(', ') || 'Sin dato'}</dd>
    </div>)}
  </dl>;
}

export default function CatalogPage() {
  const [itemId, setItemId] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inFlight = useRef(false);

  const search = async (event) => {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setSuccess('');
    setResult(null);
    try {
      const { data } = await api.get('/partner/catalog/candidates', {
        params: { item_id: itemId.trim().toUpperCase() },
      });
      setResult(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const relink = async (candidate) => {
    if (inFlight.current || !result) return;
    const approved = window.confirm(
      `CAMBIO REAL EN MERCADO LIBRE\n\nPublicación: ${result.item_id} — ${result.title}\n` +
      `Catálogo actual: ${result.catalog_product_id || 'Sin catálogo'}\n` +
      `Nuevo catálogo: ${candidate.id} — ${candidate.name}\n\n` +
      '¿Confirmas que revisaste marca, modelo, variante y especificaciones, y que corresponden exactamente al producto vendido? Este cambio afecta la competencia real de la publicación.'
    );
    if (!approved) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/partner/catalog/relink', {
        item_id: result.item_id,
        new_catalog_product_id: candidate.id,
        expected_catalog_product_id: result.catalog_product_id,
        confirmed: true,
      }, { params: { account_id: result.account_id } });
      setSuccess(`Mercado Libre aceptó el cambio de ${result.item_id} a ${candidate.id}. Consulta nuevamente para ver el estado actualizado.`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      // Discard the old association even on timeout: ML may have applied it.
      setResult(null);
      inFlight.current = false;
      setBusy(false);
    }
  };

  return <DashboardLayout title="Catálogo ML" subtitle="Revisa y cambia la asociación de una publicación" icon={BookOpen}>
    <div className="mb-5 flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">
      <AlertTriangle className="shrink-0 mt-1" size={20} />
      <p className="text-sm leading-6">Esta acción cambia el catálogo real en Mercado Libre y afecta la competencia de tu publicación.
        Los resultados son candidatos por título, no coincidencias verificadas. Compara marca, modelo, EAN, capacidad, color y variante antes de confirmar.</p>
    </div>
    <form onSubmit={search} className="mb-5 flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="catalog-item" className="mb-2 block text-sm text-slate-300">ID de publicación de la cuenta seleccionada</label>
        <input id="catalog-item" value={itemId} onChange={e => { setItemId(e.target.value); setResult(null); }}
          required pattern="[A-Za-z]{3}[0-9]+" placeholder="MLM123456789" disabled={busy}
          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-white" />
      </div>
      <button disabled={busy} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-500 disabled:opacity-50">
        {busy ? 'Procesando…' : 'Consultar candidatos'}
      </button>
    </form>
    {error && <p role="alert" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-200">{error}</p>}
    {success && <p role="status" className="mb-4 rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-green-200">{success}</p>}
    {result && <div className="space-y-5">
      <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
        <h2 className="text-lg font-semibold text-white">{result.title}</h2>
        <p className="mt-1 text-sm text-slate-400">{result.item_id} · Catálogo actual: {result.catalog_product_id || 'Sin catálogo'}</p>
        <Attributes attributes={result.attributes} />
        <h3 className="mt-5 font-medium text-white">Competencia actual</h3>
        {!result.competitors.length ? <p className="mt-2 text-sm text-slate-400">Sin datos disponibles de competencia; puede no haber catálogo o permiso de lectura.</p> :
          <ul className="mt-2 space-y-1 text-sm text-slate-300">{result.competitors.map((competitor, index) =>
            <li key={`${competitor.item_id}-${index}`}>{competitor.item_id} · Precio: {competitor.precio ?? 'Sin dato'} · {competitor.envio_gratis ? 'Envío gratis' : 'Sin envío gratis'}</li>
          )}</ul>}
      </section>
      <section aria-label="Candidatos alternativos">
        <h2 className="mb-3 text-lg font-semibold text-white">Candidatos alternativos ({result.candidates.length})</h2>
        {!result.candidates.length && <p className="text-slate-400">No se encontraron candidatos alternativos activos para este título.</p>}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {result.candidates.map(candidate => <article key={candidate.id} className="flex flex-col rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            {candidate.pictures?.[0]?.url && <img src={candidate.pictures[0].url} alt="" loading="lazy" className="mb-3 h-32 w-32 rounded-lg bg-white object-contain" />}
            <h3 className="font-medium text-white">{candidate.name}</h3>
            <p className="mt-1 text-sm text-slate-400">{candidate.id} · {candidate.domain_id}</p>
            <Attributes attributes={candidate.attributes} />
            {!candidate.attributes?.length && <p className="mt-3 text-sm text-amber-200">Sin ficha técnica en el resultado. Verifica el producto directamente en Mercado Libre.</p>}
            <div className="mt-auto pt-5">
              <button onClick={() => relink(candidate)} disabled={busy} className="w-full rounded-lg border border-amber-500/40 px-3 py-2 text-sm font-medium text-amber-200 hover:bg-amber-500/10 disabled:opacity-50">Revisar y confirmar cambio a {candidate.id}</button>
            </div>
          </article>)}
        </div>
      </section>
    </div>}
  </DashboardLayout>;
}
